import { has, pick } from "lodash";
import { from, Observable, of, throwError } from "rxjs";
import { store } from "..";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { BillingActions } from "../billing/actions";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { processErrorFromPromise } from "../errors/utils";
import { successNotificationFromPromise } from "../notifications/utils";
import { UserActions } from "../users/actions";
import { WalletsActions } from "./actions";
import { WalletsDTO } from "./models/dto";

export namespace WalletsService {
  const api = new API("auth");

  export function getWalletNonce(address: string): Promise<string> {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return Promise.reject(new Error("User not logged in"));

    return api
      .get(
        `v1/users/${login.data.accessToken.credentialsId}/wallets/${address}/nonce`
      )
      .responseType("json")
      .then((response) => response.body.nonce)
      .catch<string>(processErrorFromPromise);
  }

  export function addWallet(
    wallet: WalletsDTO.Requests.AddSingle.T
  ): Promise<WalletsDTO.Responses.AddSingle.T> {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return Promise.reject(new Error("User not logged in"));

    return api
      .post(`v1/users/${login.data.accessToken.credentialsId}/wallets/verified`)
      .send(wallet)
      .responseType("json")
      .then(successNotificationFromPromise("wallet-added"))
      .then((response) => response.body)
      .then((body) => {
        store.dispatch(WalletsActions.resetWallets());
        store.dispatch(BillingActions.resetBalance());
        store.dispatch(UserActions.resetUserDetails());
        return body;
      })
      .catch<WalletsDTO.Responses.AddSingle.T>(processErrorFromPromise);
  }

  export function removeWallet(
    id: WalletsDTO.Responses.GetSingle.T["id"]
  ): Promise<void> {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return Promise.reject(new Error("User not logged in"));

    return api
      .delete(`v1/users/${login.data.accessToken.credentialsId}/wallets/${id}`)
      .responseType("json")
      .then(successNotificationFromPromise("wallet-removed"))
      .then((response) => response.body)
      .then((body) => {
        store.dispatch(WalletsActions.resetWallets());
        return body;
      })
      .catch<void>(processErrorFromPromise);
  }

  export function getWallets(
    pagination: Pageable.T<keyof WalletsDTO.Responses.GetSingle.T> = {}
  ): Observable<
    FetchableError | FetchableSuccess<WalletsDTO.Responses.GetAll.T>
  > {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return throwError(new Error("User not logged in"));

    return from(
      api
        .get(`v1/users/${login.data.accessToken.credentialsId}/wallets`)
        .query(Pageable.getQueryParam(pagination))
        .then<FetchableSuccess<WalletsDTO.Responses.GetAll.T>>((response) => ({
          status: "ready",
          data: response.body,
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getTotalBalance(): Observable<
    FetchableError | FetchableSuccess<WalletsDTO.Responses.GetTotalBalance.T>
  > {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return throwError(new Error("User not logged in"));

    return from(
      api
        .get(`v1/users/${login.data.accessToken.credentialsId}/wallets/balance`)
        .then<FetchableSuccess<WalletsDTO.Responses.GetTotalBalance.T>>(
          (response) => ({
            status: "ready",
            data: response.body.balance,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getWallet(
    id: WalletsDTO.Responses.GetSingle.T["id"]
  ): Promise<WalletsDTO.Responses.GetSingle.T> {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return Promise.reject(new Error("User not logged in"));

    const { walletMap } = store.getState().wallets;

    if (has(walletMap, id) && walletMap[id] !== "LOADING") {
      return Promise.resolve(
        //TOOD fix for concurrency
        walletMap[id] as WalletsDTO.Responses.GetSingle.T
      );
    }

    store.dispatch(WalletsActions.getSingleWallet(id));
    return api
      .get(`v1/users/${login.data.accessToken.credentialsId}/wallets/${id}`)
      .then<WalletsDTO.Responses.GetSingle.T>((response) => {
        store.dispatch(
          WalletsActions.setSingleWallet({
            id,
            result: response.body,
          })
        );
        return response.body;
      })
      .catch<WalletsDTO.Responses.GetSingle.T>(processErrorFromPromise);
  }

  export function queryWallet(
    wallet: WalletsDTO.Requests.QueryWallet.T
  ): Promise<WalletsDTO.Responses.QueryWallet.T> {
    return api
      .get(`v1/users/wallets/exists`)
      .query(pick(wallet, "chain", "address"))
      .then<WalletsDTO.Responses.QueryWallet.T>((response) => {
        return response.body;
      })
      .catch<WalletsDTO.Responses.QueryWallet.T>(processErrorFromPromise)
  }

  export function getWalletsSummary(): Observable<
    FetchableError | FetchableSuccess<WalletsDTO.Responses.GetWalletsSummary.T>
  > {
    const login = store.getState().auth.login;
    if (login.status !== "ready") {
      return of({
        status: "error",
        err: new Error("User not logged in"),
      });
    }

    return from(
      api
        .get(`v1/users/${login.data.accessToken.credentialsId}/wallets/metrics`)
        .then<FetchableSuccess<WalletsDTO.Responses.GetWalletsSummary.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function refreshLoyalty(): Promise<WalletsDTO.Responses.RefreshWallet.T> {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return Promise.reject(new Error("User not logged in"));

    return api
      .post(
        `v1/users/${login.data.accessToken.credentialsId}/skrt-balance/refresh`
      )
      .then<WalletsDTO.Responses.RefreshWallet.T>((response) => {
        if (!Array.isArray(response.body.levels)) {
          return {
            level: undefined,
            believer: false,
          };
        } else {
          const filtered = response.body.levels
            .map((x: string) => x.toUpperCase())
            .filter((x: string) => x !== "BELIEVER" && x !== "NONE");

          return {
            believer: response.body.levels.includes("BELIEVER"),
            level: filtered.length < 1 ? undefined : filtered[0],
          };
        }
      })
      .catch<WalletsDTO.Responses.RefreshWallet.T>(processErrorFromPromise);
  }
}
